import { BaseOperations } from '@/lib/storage/base-opreations.ts';

import { LocalDatabase } from './local-database';
import {
  BaseRangeOptions,
  isZSetQueuePayload,
  RemoteClient,
  SyncQueueItem,
  ZSetQueuePayload,
  ZSetResponse
} from './types';
import { syncEventEmitter } from './sync-event-emitter.ts';

type SyncOperation = 'zadd' | 'zrem' | 'zremrangebyscore';

export class ZSetOperations extends BaseOperations {
  constructor(
    db: LocalDatabase,
    remoteClient: RemoteClient | null
  ) {
    super(db, remoteClient, ['zadd', 'zrem', 'zremrangebyscore']);
  }

  /**
   * Add a member to a sorted set, replacing if exists
   */
  async zadd<T>(key: string, score: number, member: T): Promise<void> {
  try {
    // Use updateLocalZSetFromResults to handle the local storage update
    await this.updateLocalZSetFromResults(key, [{
      member,
      score
    }]);

    // Handle remote sync and device notification in the background
    await this.syncInBackground(key, { score, member }, 'zadd').catch(error =>
      console.warn('Background sync failed:', error)
    );
  } catch (error) {
    console.error('Failed to add/update member:', error);
    throw error;
  }
}

  async zrem<T>(key: string, member: T): Promise<void> {
    try {
      // Create memberKey
      const memberKey = typeof member === 'string'
        ? member
        : JSON.stringify(member);

      // Remove from local storage
      const deleteCount = await this.db.zsetStore
        .where({
          setKey: key,
          memberKey: memberKey
        })
        .delete();

      if (deleteCount === 0) {
        console.warn('No matching member found for deletion');
      }

      // Handle remote sync
      await this.syncInBackground(key, { member }, 'zrem').catch(error =>
        console.warn('Background sync failed:', error)
      );
    } catch (error) {
      console.error('Failed to remove member:', error);
      throw error;
    }
  }

  /**
   * Remove members from sorted set by score range
   */
  async zremrangebyscore(key: string, min: number, max: number): Promise<void> {
    // Update local storage first
    await this.db.zsetStore
      .where('setKey')
      .equals(key)
      .and(item => item.score >= min && item.score <= max)
      .delete();

    // Handle remote sync in the background
    await this.syncInBackground(
      key,
      { min, max },
      'zremrangebyscore'
    ).catch(error =>
      console.warn('Background sync failed:', error)
    );
  }


  async zrangeByRank<T>(
    key: string,
    start: number,
    end: number,
    options: BaseRangeOptions = {}
  ): Promise<ZSetResponse<T>[]> {
    // Helper function to fetch from remote and update cache
    const fetchAndUpdateCache = async (): Promise<ZSetResponse<T>[]> => {
      if (!this.remoteClient) return [];

      try {
        const results = await this.fetchWithTimeout(() =>
          this.remoteClient!.zrange<T>(key, start, end, options)
        );

        await this.updateLocalZSetFromResults<T>(key, results);
        return results;
      } catch (error) {
        console.warn('Remote zrange failed:', error);
        return [];
      }
    };

    // If force refresh is requested, and we're online, try remote first
    if (options.forceRefresh && navigator.onLine && this.remoteClient) {
      const results = await fetchAndUpdateCache();
      if (results.length > 0 || options.forceRefresh) {
        // Emit data source event
        syncEventEmitter.emitDataSource(key, 'remote');
        return results;
      }
      // Only fall through to cache if not force refresh
    }

    // Get from local storage
    const localResults = await this.getLocalZRange<T>(key, start, end, options);

    // If not in cache, and we haven't tried remote yet, try remote
    if (localResults.length === 0 && navigator.onLine && this.remoteClient && !options.forceRefresh) {
      const results = await fetchAndUpdateCache();
      if (results.length > 0) {
        // Emit data source event
        syncEventEmitter.emitDataSource(key, 'remote');
        return results;
      }
    }

    // Returning cached data
    if (localResults.length > 0) {
      syncEventEmitter.emitDataSource(key, 'cache');
    }

    return localResults;
  }


  // Enhanced getLocalZRange with better error handling and type safety
  private async getLocalZRange<T>(
    key: string,
    start: number | string,
    end: number | string,
    options: BaseRangeOptions
  ): Promise<ZSetResponse<T>[]> {
    try {
      const items = await this.db.zsetStore
        .where('setKey')
        .equals(key)
        .toArray();

      if (items.length === 0) return [];

      // Sort items by score
      items.sort((a, b) => options.rev ? b.score - a.score : a.score - b.score);

      // Handle numeric indices
      const startIdx = typeof start === 'number' ? start : 0;
      const endIdx = typeof end === 'number' ? end : items.length - 1;

      // Redis uses inclusive indices on both ends
      // Negative indices: -1 = last element, -2 = second-to-last, etc.
      const actualStart = startIdx < 0 ? items.length + startIdx : startIdx;
      // For positive end indices, add 1 to make slice() inclusive (since slice is exclusive at end)
      const actualEnd = endIdx < 0 ? items.length + endIdx + 1 : endIdx + 1;

      const slicedItems = items.slice(
        Math.max(0, actualStart),
        Math.min(items.length, actualEnd)
      );

      return slicedItems.map(item => ({
        member: item.member as T,
        ...(options.withScores && { score: parseFloat(item.score.toString()) })
      }));
    } catch (error) {
      console.error('Error fetching local zrange:', error);
      return [];
    }
  }


  /**
   * Get a range of members from a sorted set by score with caching support
   */
  async zrangeByScore<T>(
    key: string,
    min: number | '-inf' | '+inf',
    max: number | '-inf' | '+inf',
    options: BaseRangeOptions = {}
  ): Promise<ZSetResponse<T>[]> {
    // Helper function to fetch from remote and update cache
    const fetchAndUpdateCache = async (): Promise<ZSetResponse<T>[]> => {
      if (!this.remoteClient) return [];

      try {
        const results = await this.fetchWithTimeout(() =>
          this.remoteClient!.zrangeByScore<T>(key, min, max, options)
        );

        await this.updateLocalZSetFromResults<T>(key, results);
        return results;
      } catch (error) {
        console.warn('Remote zrangeByScore failed:', error);
        return [];
      }
    };

    // If force refresh is requested, and we're online, try remote first
    if (options.forceRefresh && navigator.onLine && this.remoteClient) {
      const results = await fetchAndUpdateCache();
      if (results.length > 0 || options.forceRefresh) {
        // Emit data source event
        syncEventEmitter.emitDataSource(key, 'remote');
        return results;
      }
      // Only fall through to cache if not force refresh
    }

    // Get from local storage
    const localResults = await this.getLocalZRangeByScore<T>(key, min, max, options);

    // If not in cache, and we haven't tried remote yet, try remote
    if (localResults.length === 0 && navigator.onLine && this.remoteClient && !options.forceRefresh) {
      const results = await fetchAndUpdateCache();
      if (results.length > 0) {
        // Emit data source event
        syncEventEmitter.emitDataSource(key, 'remote');
        return results;
      }
    }

    // Returning cached data
    if (localResults.length > 0) {
      syncEventEmitter.emitDataSource(key, 'cache');
    }

    return localResults;
  }

  private async getLocalZRangeByScore<T>(
    key: string,
    min: number | '-inf' | '+inf',
    max: number | '-inf' | '+inf',
    options: BaseRangeOptions
  ): Promise<ZSetResponse<T>[]> {
    try {
      const items = await this.db.zsetStore
        .where('setKey')
        .equals(key)
        .toArray();

      if (items.length === 0) return [];

      // Parse min/max values and handle exclusive ranges
      const parseRange = (value: number | string): { value: number; exclusive: boolean } => {
        if (typeof value === 'number') {
          return { value, exclusive: false };
        }
        if (value === '-inf') {
          return { value: -Infinity, exclusive: false };
        }
        if (value === '+inf') {
          return { value: Infinity, exclusive: false };
        }
        if (typeof value === 'string' && value.startsWith('(')) {
          return {
            value: parseFloat(value.substring(1)),
            exclusive: true
          };
        }
        return {
          value: parseFloat(String(value)),
          exclusive: false
        };
      };

      const minRange = parseRange(min);
      const maxRange = parseRange(max);

      // Filter items based on score range
      const filteredItems = items.filter(item => {
        const score = parseFloat(item.score.toString());
        const minCheck = minRange.exclusive ? score > minRange.value : score >= minRange.value;
        const maxCheck = maxRange.exclusive ? score < maxRange.value : score <= maxRange.value;
        return minCheck && maxCheck;
      });

      // Sort items by score
      filteredItems.sort((a, b) =>
        options.rev ? b.score - a.score : a.score - b.score
      );

      // Map to response format
      return filteredItems.map(item => ({
        member: item.member as T,
        ...(options.withScores && { score: parseFloat(item.score.toString()) })
      }));
    } catch (error) {
      console.error('Error fetching local zrangeByScore:', error);
      return [];
    }
  }


  /**
   * Get a range of members from a sorted set by lexicographical order with caching support
   */
  async zrangeByLex<T>(
    key: string,
    min: `(${string}` | `[${string}` | '-' | '+',
    max: `(${string}` | `[${string}` | '-' | '+',
    options: BaseRangeOptions = {}
  ): Promise<ZSetResponse<T>[]> {
    // Helper function to fetch from remote and update cache
    const fetchAndUpdateCache = async (): Promise<ZSetResponse<T>[]> => {
      if (!this.remoteClient) return [];

      try {
        const results = await this.fetchWithTimeout(() =>
          this.remoteClient!.zrangeByLex<T>(key, min, max, options)
        );

        await this.updateLocalZSetFromResults<T>(key, results);
        return results;
      } catch (error) {
        console.warn('Remote zrangeByLex failed:', error);
        return [];
      }
    };

    // If force refresh is requested, and we're online, try remote first
    if (options.forceRefresh && navigator.onLine && this.remoteClient) {
      const results = await fetchAndUpdateCache();
      if (results.length > 0 || options.forceRefresh) {
        return results;
      }
      // Only fall through to cache if not force refresh
    }

    // Get from local storage
    const localResults = await this.getLocalZRangeByLex<T>(key, min, max, options);

    // If not in cache, and we haven't tried remote yet, try remote
    if (localResults.length === 0 && navigator.onLine && this.remoteClient && !options.forceRefresh) {
      const results = await fetchAndUpdateCache();
      if (results.length > 0) {
        return results;
      }
    }

    return localResults;
  }


  private async getLocalZRangeByLex<T>(
    key: string,
    min: `(${string}` | `[${string}` | '-' | '+',
    max: `(${string}` | `[${string}` | '-' | '+',
    options: BaseRangeOptions
  ): Promise<ZSetResponse<T>[]> {
    try {
      const items = await this.db.zsetStore
        .where('setKey')
        .equals(key)
        .toArray();

      if (items.length === 0) return [];

      const compare = (a: string, b: string): number => a.localeCompare(b);

      // Helper function to parse range bounds
      const parseRange = (bound: string): { value: string; exclusive: boolean } => {
        if (bound === '-') return { value: '', exclusive: false };
        if (bound === '+') return { value: '\uffff', exclusive: false };

        const exclusive = bound.startsWith('(');
        const value = bound.slice(1); // Remove ( or [ prefix
        return { value, exclusive };
      };

      const minRange = parseRange(min);
      const maxRange = parseRange(max);

      // Filter items based on lexicographical range
      const filteredItems = items.filter(item => {
        const member = String(item.member);
        const minCheck = minRange.exclusive
          ? compare(member, minRange.value) > 0
          : compare(member, minRange.value) >= 0;
        const maxCheck = maxRange.exclusive
          ? compare(member, maxRange.value) < 0
          : compare(member, maxRange.value) <= 0;
        return minCheck && maxCheck;
      });

      // Sort items lexicographically
      filteredItems.sort((a, b) => {
        const compareResult = compare(
          String(a.member),
          String(b.member)
        );
        return options.rev ? -compareResult : compareResult;
      });

      // Map to response format
      return filteredItems.map(item => ({
        member: item.member as T,
        ...(options.withScores && { score: parseFloat(item.score.toString()) })
      }));
    } catch (error) {
      console.error('Error fetching local zrangeByLex:', error);
      return [];
    }
  }

  private createMemberKey(member: unknown) {
    // Create a string key for the member
    return typeof member === 'string'
      ? member
      : JSON.stringify(member);
  }

  /**
   * Add a member to a sorted set, replacing if exists
   */
  private async updateLocalZSetFromResults<T>(
    key: string,
    results: ZSetResponse<T>[]
  ): Promise<void> {
    const timestamp = Date.now();

    try {
      await this.db.transaction('rw', this.db.zsetStore, async () => {
        for (const result of results) {
          const memberKey = this.createMemberKey(result.member);

          // Check for existing entry
          const existing = await this.db.zsetStore
            .where(['setKey', 'memberKey'])
            .equals([key, memberKey])
            .first();

          if (existing) {
            // Update existing entry
            await this.db.zsetStore.where(['setKey', 'memberKey'])
              .equals([key, memberKey])
              .modify({
                score: result.score ?? 0,
                timestamp
              });
          } else {
            // Add new entry
            await this.db.zsetStore.put({
              setKey: key,
              memberKey,
              member: result.member,
              score: result.score ?? 0,
              timestamp
            });
          }
        }
      });
    } catch (error) {
      console.error('Failed to update local ZSet:', error);
    }
  }

  private async syncInBackground(
    key: string,
    payload: ZSetQueuePayload,
    operation: SyncOperation
  ): Promise<void> {
    if (navigator.onLine && this.remoteClient) {
      try {
        switch (operation) {
          case 'zadd': {
            const { score, member } = payload;
            if (score === undefined || !member) {
              throw new Error('Invalid zadd payload');
            }
            await this.remoteClient.zadd(key, { score, member });
            break;
          }
          case 'zrem': {
            const { member } = payload;
            if (!member) {
              throw new Error('Invalid zrem payload');
            }
            await this.remoteClient.zrem(key, member);
            break;
          }
          case 'zremrangebyscore': {
            const { min, max } = payload;
            if (min === undefined || max === undefined) {
              throw new Error('Invalid zremrangebyscore payload');
            }
            await this.remoteClient.zremrangebyscore(key, min, max);
            break;
          }
        }
      } catch (error) {
        console.warn(`Background sync failed for ${operation}:`, error);
        await this.queueOperation(operation, key, payload);
      }
    } else {
      await this.queueOperation(operation, key, payload);
    }
  }

  protected async executeRemoteOperation(item: SyncQueueItem): Promise<void> {
    if (!this.remoteClient) {
      throw new Error('Remote client not available');
    }

    if (!isZSetQueuePayload(item.operation, item.payload)) {
      throw new Error('Not a valid zset payload in execute remote operation');
    }

    try {
      switch (item.operation) {
        case 'zadd': {
          const { score, member } = item.payload;
          if (score === undefined || !member) {
            throw new Error('Invalid zadd payload in queue');
          }
          await this.remoteClient.zadd(item.key, { score, member });
          break;
        }
        case 'zrem': {
          const { member } = item.payload;
          if (!member) {
            throw new Error('Invalid zrem payload in queue');
          }
          await this.remoteClient.zrem(item.key, member);
          break;
        }
        case 'zremrangebyscore': {
          const { min, max } = item.payload;
          if (min === undefined || max === undefined) {
            throw new Error('Invalid zremrangebyscore payload in queue');
          }
          await this.remoteClient.zremrangebyscore(item.key, min, max);
          break;
        }
      }
    } catch (error) {
      console.error('Failed to execute remote operation:', {
        operation: item.operation,
        key: item.key,
        error
      });
      throw error;
    }
  }

  public async syncFromRemote(key: string): Promise<boolean> {
    try {
      if (!this.remoteClient || !navigator.onLine) {
        console.warn('Remote client is not available for syncFromRemote');
        return false;
      }

      // Fetch the entire ZSet from the remote client
      const remoteZSet = await this.remoteClient.zrange(key, 0, -1, { withScores: true });

      // Clear the local store for the given key
      await this.db.zsetStore.where('setKey').equals(key).delete();

      // Update the local database with the retrieved ZSet
      for (const item of remoteZSet) {
        await this.db.zsetStore.put({
          setKey: key,
          memberKey: this.createMemberKey(item.member),
          member: item.member,
          score: item.score ?? 0,
          timestamp: Date.now()
        });
      }

      console.log(`Successfully synced ${key} from remote`);
      return true;
    } catch (error) {
      console.error('Failed to sync ZSet from remote:', error);
      return false;
    }
  }
}